import { Translate, useTranslate, NextLink } from '~/i18n';
import { ExperienceDetailsProps } from '~/interfaces/experience';
import { CheckIcon, CloseIcon, MarkerMap, StarIcon, ArrowDownIcon, ShareIcon } from '~/svgs/common';
import Container from '../common/container';
import { DetailsCollapsableSection } from './collapsable-section';
import { ExperienceImageGallery } from './image-gallery';
import Itinerary from './itinerary';
import dynamic from 'next/dynamic';
import Button from '../common/button';
import ExperienceReviews from './reviews';
import Image from 'next/image';
import { formatNumber } from '~/utils/format-price';
import { BookingDetailsCTA } from './cta';
import Breadcrumb from '../common/breadcrumb';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import cn from 'classnames';
import {
   expDetailsGetDirectionsBtnEvent,
   expDetailsShareExpBtnEvent,
   expDetailsOrganizerPageBtnEvent,
} from '~/utils/mixpanel-events/experience-details';
import { isJarir } from '~/utils/theme';
import JarirSignup from 'components/jarir/modal';
import { UserType } from '~/interfaces/user';
import { useAuth } from '~/auth';
import toast from 'react-hot-toast';
import Axios from '~/utils/axios';
import { sentryCaptureException } from '~/errors/sentry';
// const GoogleMap = dynamic(() => import('google-map-react'), {
//    ssr: false,
//    loading() {
//       return (
//          <div className="text-gray-500">
//             <Translate id="common:loading" />
//          </div>
//       );
//    },
// });

const ShareModal = dynamic(() => import('../common/share-modal'), {
   ssr: false,
});

// const Marker: React.FC<any> = () => {
//    return (
//       <div className="relative">
//          <div className="absolute inset-0">
//             <MarkerMap />
//          </div>
//       </div>
//    );
// };

type Props = {
   data: ExperienceDetailsProps;
   reviews: any;
};
export const ExperienceDetailsContent = ({ data, reviews }: Props) => {
   const { locale, translate } = useTranslate();
   const router = useRouter();
   const { authenticated, user, updateUser } = useAuth<UserType>();
   const lat = +data.latlng?.split(',')[0];
   const lng = +data.latlng?.split(',')[1];
   const hasCollapse = (data.rt_description.length || data.description.length) > 500;
   const [open, setOpen] = useState(!hasCollapse);
   const collapse = () => setOpen(true);
   const [sharing, setShare] = useState(false);
   const share_title = data[`meta_title_${locale}`] || translate({ id: 'seo:home title' });
   const [loading, setLoading] = useState(false);
   const [openJarirSignup, setOpenJarirSignup] = useState(false);

   const share = () => {
      expDetailsShareExpBtnEvent({
         experienceId: data.venue_id,
         experienceName: data.name,
         providerName: data.organizer_name,
      });
      if (navigator.share) {
         navigator.share({
            url: window.location.href,
            title: share_title,
         });
      } else {
         setShare(true);
      }
   };

   const stopSharing = () => setShare(false);

   const LocalizedList = ({ icon, list }: { list: any[]; icon?: React.ReactNode }) => {
      return (
         <ul>
            {list?.map((item, index) => (
               <li className="flex mt-2 text-lg first:mt-0" key={index}>
                  <div className="w-10">{icon}</div>
                  <span>{item[locale]}</span>
               </li>
            ))}
         </ul>
      );
   };

   const getBookingType = () => {
      const bookingTypeData: any = {
         internal: {
            image: '/icons/experiences/thunder.svg',
            bgColor: 'rgba(255, 193, 7, 0.25)',
            iconBgcolor: 'rgba(255, 193, 7)',
         },
         external: {
            image: '/icons/experiences/external.svg',
            bgColor: 'rgba(227, 254, 254, 0.25)',
            iconBgcolor: 'rgba(227, 254, 254)',
         },
         '24_hours_confirmation': {
            image: '/icons/experiences/24-hours.svg',
            bgColor: 'rgba(102, 16, 242, 0.25)',
            iconBgcolor: 'rgba(102, 16, 242)',
         },
         online_experience: {
            bgColor: 'rgba(253, 126, 20, 0.25)',
            iconBgcolor: 'rgba(253, 126, 20)',
         },
         offline_experience: {
            image: '/icons/experiences/offline.svg',
            bgColor: 'rgba(0, 185, 216, 0.25)',
            iconBgcolor: 'rgba(0, 185, 216)',
         },
      };

      if (!data?.venue_type || !bookingTypeData[data?.venue_type]) return null;
      return (
         <div className="flex w-full px-2 my-4 bg-gray-100 rounded-lg">
            <div className="flex items-center py-2 mx-auto">
               <div
                  className={cn(
                     'flex justify-center w-6 h-6 rounded-md ltr:mr-3 rtl:ml-3',
                     data?.venue_type === 'offline_experience' ? 'w-8' : ''
                  )}
                  style={{
                     backgroundColor: bookingTypeData[data?.venue_type]?.iconBgcolor,
                  }}>
                  {bookingTypeData[data?.venue_type]?.image ? (
                     <img
                        width="16px"
                        alt="venue type icon"
                        src={bookingTypeData[data?.venue_type]?.image}
                     />
                  ) : null}
               </div>
               <div className="">
                  {/* <div className="flex items-center">
                     <h2 className="text-base font-semibold">{data?.venue_type_details?.long_title}</h2>
                  </div> */}
                  <p className="mt-1 text-sm text-gray-700">
                     {isJarir
                        ? data?.venue_type_details?.description_jarir
                        : data?.venue_type_details?.description}
                  </p>
               </div>
            </div>
         </div>
      );
   };

   const updateWishlist = async (is_logged_in?: boolean) => {
      const _venue_id = data?.venue_id;
      if (!authenticated && !is_logged_in) {
         setOpenJarirSignup(true);
         return;
      }
      if (loading) return;
      setLoading(true);
      try {
         const { data } = await Axios.post('/user/wishlist', {
            venue_id: _venue_id,
         });

         if (is_logged_in) {
            window?.location?.reload();
         }

         const is_wishlist =
            data?.message === 'Successfully Removed from wishlist' ||
            user?.wishlist?.includes(_venue_id);

         if (data.status === 'Success') {
            setLoading(false);
            toast(
               <span>
                  {is_wishlist
                     ? translate({ id: 'auth:removed from your wishlist' })
                     : translate({ id: 'auth:added to your wishlist' })}
               </span>,
               {
                  icon: is_wishlist ? (
                     <img
                        src="/icons/profile/heart-black-border.png"
                        title={translate({ id: 'auth:add to wishlist' })}
                        className="w-6 mx-0.5 my-0.5"
                     />
                  ) : (
                     <img
                        src="/icons/profile/heart-filled.png"
                        title={translate({ id: 'auth:remove from wishlist' })}
                        className="w-6 mx-0.5 my-0.5"
                     />
                  ),
                  style: {
                     background: '#fef6d6',
                  },
               }
            );
            if (is_wishlist) {
               const _wishlist = user?.wishlist?.filter(item => item !== _venue_id);
               updateUser({ ...user, wishlist: _wishlist });
            } else {
               const _wishlist = [...user.wishlist, ...[_venue_id]];
               updateUser({ ...user, wishlist: _wishlist });
            }
            if (is_logged_in) {
               window?.location?.reload();
            }
         } else {
            throw new Error(translate({ id: 'business:failed toaster' }));
         }
      } catch (error) {
         setLoading(false);
         sentryCaptureException(error, { tags: { journey: 'business' } });
      }
   };

   const favIcon = useMemo(() => {
      const is_wishlist = user?.wishlist?.includes(data.venue_id);
      return is_wishlist ? (
         <>
            <img
               src="/icons/profile/heart-filled.png"
               title={translate({ id: 'auth:remove from wishlist' })}
               className="w-6 mx-0.5 my-0.5"
            />{' '}
            <span className="hidden md:block ltr:ml-2 rtl:mr-2">
               <Translate id="auth:remove from wishlist" />
            </span>
         </>
      ) : (
         <>
            <>
               <img
                  src="/icons/profile/heart-black-border.png"
                  title={translate({ id: 'auth:add to wishlist' })}
                  className="hidden md:block w-6 mx-0.5 my-0.5"
               />
               <img
                  src="/icons/profile/heart.png"
                  title={translate({ id: 'auth:add to wishlist' })}
                  className="block md:hidden w-6 mx-0.5 my-0.5"
               />
            </>{' '}
            <span className="hidden md:block ltr:ml-2 rtl:mr-2">
               <Translate id="auth:add to wishlist" />
            </span>
         </>
      );
   }, [translate, user, data.venue_id]);

   return (
      <div className="pt-12 md:pt-10 lg:pt-8 md:container md:mx-auto">
         <Container>
            <ShareModal sharing={sharing} share_title={share_title} stopSharing={stopSharing} />
            {!authenticated && (
               <JarirSignup
                  modalTitle={translate({ id: 'common:login to continue' })}
                  open={openJarirSignup}
                  onClose={() => {
                     setOpenJarirSignup(false);
                     setLoading(false);
                  }}
                  openOtp={value => {
                     setOpenJarirSignup(value);
                     setTimeout(() => {
                        updateWishlist(true);
                     }, 500);
                  }}
                  preventRedirect
               />
            )}
            <div className="flex">
               <Breadcrumb
                  className="flex-grow py-4 text-sm tablet:pt-6"
                  items={[
                     { href: `/${data.region?.slug}`, name: data.region?.name },
                     {
                        href: `/${data.region?.slug}/experiences`,
                        name: translate({ id: 'common:experiences' }),
                     },
                     {
                        href: `/${data.region?.slug}/experiences/c/${
                           data.category?.[`slug_${locale}`]
                        }`,
                        name: data.category?.[`title_${locale}`],
                     },
                     {
                        name: data.name,
                        href: router.asPath,
                        disabled: true,
                        className: 'text-gray-400',
                     },
                  ]}
               />
               <div className="relative hidden md:block lg:top-10">
                  <div className="flex flex-row-reverse">
                     <button
                        onClick={share}
                        className="z-10 px-3 py-1 mt-5 text-sm border rounded-md text-primary-light border-primary-light hover:bg-primary-disabled tablet:block focus:outline-none focus:bg-primary-disabled">
                        <span className="flex items-center">
                           <ShareIcon className="-mt-1" />
                           <span className="block ltr:ml-2 rtl:mr-2">
                              <Translate id="common:share" />
                           </span>
                        </span>
                     </button>
                     <button
                        onClick={() => updateWishlist()}
                        className="z-10 px-3 py-1 mx-2 mt-5 text-sm border rounded-md text-primary-light border-primary-light hover:bg-primary-disabled tablet:block focus:outline-none focus:bg-primary-disabled">
                        <span className="flex items-center">{favIcon}</span>
                     </button>
                  </div>
               </div>
            </div>
         </Container>
         <div className="flex flex-col mb-2 space-y-4 lg:space-y-0 lg:flex-row">
            <div className="w-full lg:ltr:pr-4 lg:rtl:pl-4">
               <div className="relative flex flex-col-reverse mt-2 tablet:flex-col">
                  <Container>
                     <div>
                        <h1 className="text-2xl font-bold lg:text-4xl">{data.name}</h1>
                        <div className="flex flex-wrap items-center rtl:pt-3">
                           {reviews.overall_rating ? (
                              <span className="flex items-center space-x-1 ltr:mr-8 rtl:ml-8 rtl:space-x-reverse">
                                 <StarIcon /> <span>{formatNumber(reviews.overall_rating, 1)}</span>
                              </span>
                           ) : null}
                           <div className="flex items-center ltr:mr-8 rtl:ml-8">
                              <MarkerMap className="-mt-1 transform scale-75 ltr:mr-2 rtl:ml-2" />
                              {data.city}
                           </div>
                           {data?.venue_type !== '24_hours_confirmation' &&
                              data?.venue_type !== 'offline_experience' && (
                                 <div className="flex items-center space-x-2 leading-none rtl:space-x-reverse">
                                    <div className="text-xs">
                                       <Translate id="booking:organized by" />
                                    </div>
                                    <div className="relative">
                                       <Image
                                          width={31}
                                          height={31}
                                          className="object-cover rounded-full"
                                          src={data.organizer_logo}
                                       />
                                    </div>
                                    <div>
                                       {data.group_id ? (
                                          <NextLink
                                             href={`/experiences/organizer/${data.group_id}`}
                                             onClick={() =>
                                                expDetailsOrganizerPageBtnEvent({
                                                   experienceId: data._id,
                                                   experienceName: data.name,
                                                   providerName: data.organizer_name,
                                                })
                                             }>
                                             {data.organizer_name}
                                          </NextLink>
                                       ) : (
                                          data.organizer_name
                                       )}
                                    </div>
                                 </div>
                              )}
                        </div>
                     </div>
                  </Container>
                  <div className="absolute z-10 flex md:hidden top-2 ltr:right-2 rtl:left-2">
                     <div
                        className="rounded-full"
                        style={{ background: 'rgba(52, 58, 64, 0.48)' }}
                        onClick={e => {
                           e.preventDefault();
                           updateWishlist();
                        }}>
                        {favIcon}
                     </div>
                     <div
                        className="rounded-full"
                        style={{ background: 'rgba(52, 58, 64, 0.48)', margin: '0px 5px' }}
                        onClick={share}>
                        <ShareIcon style={{ margin: '5px 7px', color: '#bfcdd7' }} />
                     </div>
                  </div>
                  <ExperienceImageGallery
                     images={data.optimized_images}
                     video={data.venue_video_url}
                  />
               </div>
               <div className="mx-4 lg:hidden">{getBookingType()}</div>
               <Container>
                  {data.rt_description &&
                  !data.rt_description.includes('>Overview') &&
                  !data.rt_description.includes('>لمحة عامة') ? (
                     <h2 className="my-4 text-2xl font-bold ">
                        <Translate id="common:overview" />
                     </h2>
                  ) : null}
                  <div className="mb-5">
                     <div className="relative overflow-hidden">
                        <motion.article
                           initial={{ height: hasCollapse ? 180 : 'auto' }}
                           animate={{ height: open ? 'auto' : 180 }}
                           className="prose !max-w-full ltr:pr-3 rtl:pl-3"
                           transition={{ duration: 0.25, ease: 'easeInOut' }}
                           dangerouslySetInnerHTML={{
                              __html: data.rt_description || data.description,
                           }}></motion.article>
                        {!open && (
                           <div className="absolute inset-x-0 bottom-0 h-[100px] bg-gradient-to-b from-white/25 to-white z-10"></div>
                        )}
                     </div>
                     {!open && (
                        <button
                           onClick={collapse}
                           className="flex items-center mb-4 font-bold r-more">
                           <span>
                              <Translate id="common:read more" />{' '}
                           </span>
                           <ArrowDownIcon className="flex-shrink-0 w-3 mt-1 ltr:ml-1 rtl:mr-1" />
                        </button>
                     )}
                  </div>
                  {data.itinerary?.length ? <Itinerary itinerary={data.itinerary} /> : null}
                  {data.included?.length ? (
                     <DetailsCollapsableSection title={<Translate id="booking:what is included" />}>
                        <LocalizedList list={data.included} icon={<CheckIcon />} />
                     </DetailsCollapsableSection>
                  ) : null}
                  {data.excluded?.length ? (
                     <DetailsCollapsableSection
                        title={<Translate id="booking:what is not included" />}>
                        <LocalizedList
                           list={data.excluded}
                           icon={<CloseIcon className="text-primary-error" />}
                        />
                     </DetailsCollapsableSection>
                  ) : null}
                  <div className="py-5 mt-5 border-t">
                     <div className="prose">
                        <h3>
                           <Translate id="common:location" />
                        </h3>
                     </div>
                     {lat && lng ? (
                        <div>
                           {/* <div className="w-full h-48 my-4 overflow-hidden rounded cursor-pointer">
                              <a
                                 rel="noopener noreferrer"
                                 target="_blank"
                                 href={`https://maps.google.com/?q=${lat},${lng}&z=8`}
                                 className="cursor-pointer d-block text-gray-700-color">
                                 <GoogleMap
                                    defaultCenter={{ lat, lng }}
                                    defaultZoom={13}
                                    bootstrapURLKeys={{
                                       key: process.env.NEXT_PUBLIC_GOOGLE_MAP + '',
                                    }}
                                    options={{ disableDefaultUI: true, draggable: false }}>
                                    <Marker lat={lat} lng={lng} />
                                 </GoogleMap>
                              </a>
                           </div> */}

                           <div className="flex items-center justify-between">
                              <div>
                                 <span className="block">
                                    <Translate id="booking:meeting point" />
                                 </span>
                                 <span className="text-sm font-bold"> {data.address}</span>
                              </div>
                              <Button
                                 onClick={() => {
                                    window.open(
                                       `https://maps.google.com/?q=${lat},${lng}&z=8`,
                                       '_blank'
                                    );
                                    expDetailsGetDirectionsBtnEvent({
                                       experienceId: data.venue_id,
                                       experienceName: data.name,
                                       providerName: data.organizer_name,
                                    });
                                 }}
                                 className="!py-2 font-bold">
                                 <Translate id="booking:get directions" />
                              </Button>
                           </div>
                        </div>
                     ) : null}
                  </div>
                  {data.guest_requirements?.length ? (
                     <DetailsCollapsableSection title={<Translate id="booking:what to bring" />}>
                        <LocalizedList list={data.guest_requirements} icon={<CheckIcon />} />
                     </DetailsCollapsableSection>
                  ) : null}
                  {data.refund_policy_details?.[locale] ? (
                     <DetailsCollapsableSection title={<Translate id="booking:refund policy" />}>
                        <h2 className="font-bold">{data.refund_policy}</h2>
                        <div
                           className="prose"
                           dangerouslySetInnerHTML={{ __html: data.refund_policy_details[locale] }}
                        />
                     </DetailsCollapsableSection>
                  ) : null}
                  {reviews?.total > 0 ? (
                     // <DetailsCollapsableSection title={<Translate id="common:reviews" />}>
                     <div className="pt-4 border-t">
                        <div className="prose">
                           <h3 className="flex">
                              <span className="ltr:mr-3 rtl:ml-3">
                                 <Translate id="common:reviews" />
                              </span>
                              <span className="flex items-center space-x-1 text-base font-normal rtl:space-x-reverse">
                                 <StarIcon /> <span>{formatNumber(reviews.overall_rating, 1)}</span>
                              </span>
                           </h3>
                        </div>
                        <ExperienceReviews reviews={{ ...reviews, experience_id: data._id }} />
                     </div>
                  ) : // </DetailsCollapsableSection>
                  null}
               </Container>
            </div>

            <BookingDetailsCTA
               price={data.list_price}
               discounted_price={data.discounted_price}
               id={data._id}
               venue_type={data.venue_type}
               book_online={data.book_online === 'Yes'}
               external_link={data.venue_external_link}
               is_dmc={data.is_dmc}
               is_external={data.is_external}
               gender={data.gender}
               venue_type_details={data.venue_type_details}
               share_title={data[`meta_title_${locale}`] || translate({ id: 'seo:home title' })}
               share_description={
                  data[`meta_description_${locale}`] || translate({ id: 'seo:home description' })
               }
               refund_policy={data.refund_policy}
               name={data.name}
               city={data.city}
               category_title={data.category?.title}
               organizer={data.organizer_name}
            />
         </div>
      </div>
   );
};
